import {
    Avatar,
    Button,
    Checkbox,
    Dialog,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemText,
    TextField,
    Typography,
} from "@material-ui/core";
import { AccessTime, Business, Close, DirectionsWalk } from "@material-ui/icons";
import { Alert as ToastAlert } from "@material-ui/lab";
import { CircularProgress } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { alertService } from "@/api/services/implementations/alert";
import { useDispatch } from "@/store";
import { addAlert, Severity } from "@/store/alerts/slice";
import { Alert } from "@/utilities/types/alert";

import useStyles from "./ResolveDialog.styles";


interface ResolveDialogProps {
    alertDetails: Alert;
    open: boolean;
    setOpen: (_: boolean) => void;
}

enum Tag {
    accident = "accident",
    animal = "animal",
    deterredCrime = "deterredCrime",
    employeeVendor = "employeeVendor",
    loitering = "loitering",
    suspiciousActivity = "suspiciousActivity",
    tampering = "tampering",
    theft = "threft",
    technicalIssue = "technicalIssue",
    violence = "violence",
    other = "other",
}

type TagState = Record<Tag, boolean>;

const ResolveDialog: FC<ResolveDialogProps> = ({ alertDetails: ad, open, setOpen }) => {
    const { t, i18n } = useTranslation("translation", { keyPrefix: "components.alerts.resolveDialog" });
    const initialTagState = Object.keys(Tag).reduce((state, tag) => { return { ...state, [tag]: false }; }, {} as TagState);
    const [tags, setTags] = useState<TagState>(initialTagState);
    const [noteInput, setNoteInput] = useState("");
    const [error, setError] = useState("");
    const [awaitingResolve, setAwaitingResolve] = useState<boolean>(false);

    let time = DateTime.fromJSDate(new Date(ad.alertTimestamp))
        .setLocale(i18n.language);

    if (ad.locationTimezone != null) {
        time = time.setZone(ad.locationTimezone);
    }

    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const handleNoteInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNoteInput(e.target.value);
    };

    const handleChangeTag = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Assigning the checkbox values gets around a state update bug in testing
        const name = e.target.name;
        const checked = e.target.checked;

        setTags((state) => { return { ...state, [name]: checked }; });
    };

    const handleClose = () => {
        setNoteInput("");
        setTags(initialTagState);
        setError("");
        setOpen(false);
    };

    const validateForm = () => {
        if (Object.values(tags).some(value => value === true)) {
            if (tags[Tag.other] === true && noteInput.trim() === "") {
                setError(t("errors.noteRequired"));

                return false;
            } else {
                return true;
            }
        } else {
            setError(t("errors.tagRequired"));

            return false;
        }
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        setAwaitingResolve(true);

        alertService.postResolveAlert({
            alertId: ad.alertId,
            note: noteInput.trim(),
            tags: Object.keys(tags).filter(key => tags[key as Tag] === true),
        }).then(() => {
            handleClose();
            onResolveSuccessMessage();
            history.push("/alerts");
        }).catch((e) => {
            dispatch(
                addAlert({
                    severity: Severity.Error,
                    message: t("errors.resolveFailed", { message: e.message }),
                }),
            );
        }).finally(() => setAwaitingResolve(false));
    };

    const onResolveSuccessMessage = () => {
        closeSnackbar(ad.alertId);

        const alert = (
            <ToastAlert severity="success">
                {t("toast.resolveSuccess", { alertId: ad.alertId })}
            </ToastAlert>
        );

        enqueueSnackbar(alert, {
            anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
            },
            autoHideDuration: 8000,
            preventDuplicate: true,
            variant: Severity.Success,
        });
    };

    const renderCheckboxes = Object.entries(tags).map(([type, checked], i) => {
        return (
            <Grid item xs={6} key={i}>
                <FormControlLabel
                    classes={{ root: classes.checkbox }}
                    control={<Checkbox checked={checked} onChange={handleChangeTag} />}
                    name={type}
                    label={t(`tags.${type as keyof typeof Tag}`)}
                />
            </Grid>
        );
    });

    return (
        <Dialog open={open} onClose={handleClose} classes={{ paperWidthSm: classes.dialog }} PaperProps={{ classes: { root: classes.body } }}>
            <div className={classes.body}>
                <div className={classes.head}>
                    <Typography variant="h5">{t("title")}</Typography>
                    <IconButton onClick={handleClose} className={classes.close} >
                        <Close fontSize="large" />
                    </IconButton>
                </div>
                {ad &&
                    <div className={classes.details}>
                        {(ad.locationName || ad.liveUnitName || ad.serial) &&
                            <ListItem disableGutters className={classes.detailItem}>
                                <ListItemAvatar><Avatar className={classes.detailIcon}>
                                    <Business />
                                </Avatar></ListItemAvatar>
                                <ListItemText
                                    primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                                    primary={ad.locationName}
                                    secondary={`${ad.liveUnitName ?? ""}${(ad.liveUnitName && ad.serial) ? ", " : ""}${ad.serial ?? ""}`}
                                />
                            </ListItem>
                        }
                        {(ad.alertTimestamp || ad.locationTimezone) &&
                            <ListItem disableGutters className={classes.detailItem}>
                                <ListItemAvatar><Avatar className={classes.detailIcon}>
                                    <AccessTime />
                                </Avatar></ListItemAvatar>
                                <ListItemText
                                    primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                                    primary={time.toLocaleString(DateTime.TIME_SIMPLE)}
                                    secondary={time.toLocaleParts({ timeZoneName: "longGeneric" }).pop().value}
                                />
                            </ListItem>
                        }
                        {(ad.alertType || ad.alertSubject) &&
                            <ListItem disableGutters className={classes.detailItem}>
                                <ListItemAvatar><Avatar className={classes.detailIcon}>
                                    <DirectionsWalk />
                                </Avatar></ListItemAvatar>
                                <ListItemText
                                    primaryTypographyProps={{ style: { fontWeight: "bold" } }}
                                    primary={t([`alertType.${ad.alertType!}`, "alertTypeDefault"])}
                                    secondary={ad.alertSubject && t(`alertSubject.${ad.alertSubject}`, "")}
                                />
                            </ListItem>
                        }
                    </div>
                }
                <div className={classes.form}>
                    <Grid container className={classes.checkboxColumns}>
                        {renderCheckboxes}
                    </Grid>
                    <TextField
                        className={classes.note}
                        hiddenLabel
                        id="resolve_note_input"
                        placeholder={t("note")}
                        onChange={handleNoteInput}
                        value={noteInput}
                        variant="outlined"
                        multiline
                        minRows={10}
                        inputProps={{ maxLength: 1000 }}
                    />
                </div>

                <Divider className={classes.divider} />

                <div className={classes.actions}>
                    {error !== "" && <Typography color="error">{error}</Typography>}
                    {awaitingResolve ?
                        <CircularProgress />
                        :
                        <Button onClick={handleSubmit} color="secondary" variant="contained">{t("resolveAlert")}</Button>
                    }
                </div>
            </div>
        </Dialog>
    );
};

export default ResolveDialog;
